import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, Snackbar, Alert, Tabs, Tab, Button,
} from '@mui/material';
import { getAllRoles, getAllRights, getAllRoleRights, updateRoleRight } from '../../api/roleRightApi';

const SUPER_ADMIN_ROLE_ID = 6;
const FONT = { fontFamily: "'Poppins', sans-serif" };

// Map right names to human-readable labels, grouped by sidebar section
const FEATURE_SECTIONS = [
  {
    section: 'Sales',
    features: [
      { rightName: 'AgentSalesList', label: 'My Sales / My Customers / Referrals', actions: ['view'] },
      { rightName: 'GroupSalesList', label: 'Group Sales', actions: ['view'] },
      { rightName: 'CreateSale', label: 'Create Sale', actions: ['view', 'create'] },
      { rightName: 'SalesRemarks', label: 'Sales Remarks', actions: ['view', 'create'] },
      { rightName: 'SalesPricing', label: 'Sales Pricing (Price, Discounts)', actions: ['view', 'edit'] },
    ],
  },
  {
    section: 'Commission (Agent)',
    features: [
      { rightName: 'PersonalSalesCommission', label: 'Personal PSC', actions: ['view'] },
      { rightName: 'AgentCommissionSlips', label: 'My Commission Slips', actions: ['view'] },
      { rightName: 'DownlineOverridingCommission', label: 'Downline DOC', actions: ['view'] },
      { rightName: 'TeamOverridingCommission', label: 'Team TOC', actions: ['view'] },
    ],
  },
  {
    section: 'Accounting',
    features: [
      { rightName: 'AllSalesList', label: 'All Sales / All Customers', actions: ['view', 'edit', 'create', 'delete'] },
      { rightName: 'AllSalesCommList', label: 'All Sales Commission', actions: ['view', 'edit', 'approve'] },
      { rightName: 'TotalCommission', label: 'Astern Claims', actions: ['view', 'edit', 'approve'] },
      { rightName: 'WebbeyCommission', label: 'Webbey Commission', actions: ['view'] },
      { rightName: 'CommissionSlipGenerator', label: 'Commission Slips (Generate)', actions: ['view', 'create', 'approve'] },
      { rightName: 'CommissionAmendment', label: 'Commission Amendment', actions: ['view', 'create', 'approve'] },
      { rightName: 'PendingApprovals', label: 'Pending Approvals', actions: ['view', 'approve'] },
      { rightName: 'AgentApplications', label: 'Agent Applications', actions: ['view', 'edit', 'approve'] },
    ],
  },
  {
    section: 'Incentives',
    features: [
      { rightName: 'IncentiveManagement', label: 'Manage Campaigns', actions: ['view', 'edit', 'create', 'delete'] },
      { rightName: 'IncentiveSlips', label: 'Incentive Slips', actions: ['view'] },
    ],
  },
  {
    section: 'Resources',
    features: [
      { rightName: 'NoticeBoard', label: 'Noticeboard', actions: ['view', 'edit', 'create', 'delete'] },
    ],
  },
  {
    section: 'Reports',
    features: [
      { rightName: 'SalesReport', label: 'Sales Report', actions: ['view'] },
      { rightName: 'WebbeyCommission', label: 'Webbey Report', actions: ['view'] },
      { rightName: 'AgentReport', label: 'Agent Report', actions: ['view'] },
    ],
  },
  {
    section: 'Admin',
    features: [
      { rightName: 'CreateUser', label: 'Create User', actions: ['view', 'create'] },
      { rightName: 'UserManagement', label: 'User Management', actions: ['view', 'edit', 'create', 'delete'] },
      { rightName: 'AgentHierarchy', label: 'Agent Hierarchy', actions: ['view'] },
      { rightName: 'PermissionManager', label: 'Permission Manager', actions: ['view', 'edit'] },
      { rightName: 'ApproverConfig', label: 'Approver Config', actions: ['view', 'edit'] },
      { rightName: 'ImportSales', label: 'Import Sales', actions: ['view', 'create'] },
      { rightName: 'AdjustmentEdit', label: 'Adjustment Edit (Co-broke)', actions: ['edit'] },
    ],
  },
];

const ACTION_LABELS = {
  view: 'View',
  edit: 'Edit',
  create: 'Create',
  delete: 'Delete',
  approve: 'Approve',
};

const ACTION_COLORS = {
  view: '#3B82F6',
  edit: '#F59E0B',
  create: '#10B981',
  delete: '#EF4444',
  approve: '#8B5CF6',
};

export default function PermissionManager() {
  const [roles, setRoles] = useState([]);
  const [rights, setRights] = useState([]);
  const [roleRights, setRoleRights] = useState([]);
  const [localRoleRights, setLocalRoleRights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const savedRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [rolesRes, rightsRes, rrRes] = await Promise.all([
        getAllRoles(),
        getAllRights(),
        getAllRoleRights(),
      ]);
      setRoles(rolesRes.data.filter(r => r.roleId !== 7));
      setRights(rightsRes.data);
      setRoleRights(rrRes.data);
      setLocalRoleRights(rrRes.data);
      savedRef.current = JSON.stringify(rrRes.data);
    } catch (error) {
      console.error('Error fetching permission data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasChanges = JSON.stringify(localRoleRights) !== savedRef.current;

  const getRoleRight = (roleId, rightName) => {
    return localRoleRights.find(rr => rr.roleId === roleId && rr.rightName === rightName);
  };

  const hasAction = (roleId, rightName, action) => {
    if (roleId === SUPER_ADMIN_ROLE_ID) return true;
    const rr = getRoleRight(roleId, rightName);
    if (!rr) return false;
    return (rr.actions || 'view').split(',').includes(action);
  };

  // Check if a specific feature has been modified for the active role
  const isModified = (roleId, rightName) => {
    const original = roleRights.find(rr => rr.roleId === roleId && rr.rightName === rightName);
    const local = localRoleRights.find(rr => rr.roleId === roleId && rr.rightName === rightName);
    const origActions = original?.actions || null;
    const localActions = local?.actions || null;
    return origActions !== localActions;
  };

  const handleActionToggle = (roleId, rightName, action) => {
    if (roleId === SUPER_ADMIN_ROLE_ID) return;

    const right = rights.find(r => r.rightName === rightName);
    if (!right) return;

    const rr = getRoleRight(roleId, rightName);
    const currentActions = rr ? (rr.actions || 'view').split(',') : [];
    const isCurrentlyEnabled = currentActions.includes(action);

    let newActions;
    if (isCurrentlyEnabled) {
      newActions = currentActions.filter(a => a !== action);
    } else {
      newActions = [...currentActions, action];
      if (action !== 'view' && !newActions.includes('view')) {
        newActions.unshift('view');
      }
    }

    if (action === 'view' && isCurrentlyEnabled) {
      newActions = [];
    }

    setLocalRoleRights(prev => {
      if (newActions.length === 0) {
        return prev.filter(r => !(r.roleId === roleId && r.rightName === rightName));
      }
      const actionsStr = newActions.join(',');
      const existing = prev.find(r => r.roleId === roleId && r.rightName === rightName);
      if (existing) {
        return prev.map(r => r.roleId === roleId && r.rightName === rightName ? { ...r, actions: actionsStr } : r);
      }
      return [...prev, { roleRightId: 0, roleId, roleName: '', rightId: right.rightId, rightName, actions: actionsStr }];
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Find all changes between roleRights (saved) and localRoleRights (local)
      const allRightNames = [...new Set([...FEATURE_SECTIONS.flatMap(s => s.features.map(f => f.rightName))])];

      for (const role of roles) {
        if (role.roleId === SUPER_ADMIN_ROLE_ID) continue;

        for (const rightName of allRightNames) {
          const right = rights.find(r => r.rightName === rightName);
          if (!right) continue;

          const original = roleRights.find(rr => rr.roleId === role.roleId && rr.rightName === rightName);
          const local = localRoleRights.find(rr => rr.roleId === role.roleId && rr.rightName === rightName);

          const origActions = original?.actions || null;
          const localActions = local?.actions || null;

          if (origActions === localActions) continue;

          if (!localActions) {
            await updateRoleRight({ roleId: role.roleId, rightId: right.rightId, enabled: false });
          } else {
            await updateRoleRight({ roleId: role.roleId, rightId: right.rightId, enabled: true, actions: localActions });
          }
        }
      }

      // Refresh from server
      const rrRes = await getAllRoleRights();
      setRoleRights(rrRes.data);
      setLocalRoleRights(rrRes.data);
      savedRef.current = JSON.stringify(rrRes.data);

      setSnackbar({ open: true, message: 'Permissions saved successfully', severity: 'success' });
    } catch (error) {
      console.error('Error saving permissions:', error);
      setSnackbar({ open: true, message: 'Failed to save permissions', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setLocalRoleRights(roleRights);
    setSnackbar({ open: true, message: 'Changes discarded', severity: 'info' });
  };

  if (loading) return <div style={{ padding: 24, ...FONT }}>Loading...</div>;

  const activeRole = roles[activeTab] || roles[0];
  const isSuperAdmin = activeRole?.roleId === SUPER_ADMIN_ROLE_ID;

  return (
    <div>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', ...FONT }}>
            Permission Manager
          </h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', ...FONT }}>
            Manage what each role can access and do
          </p>
        </div>
        {hasChanges && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleDiscard}
              variant="outlined"
              size="small"
              sx={{ ...FONT, textTransform: 'none', fontWeight: 600, borderColor: '#E2E8F0', color: '#64748B' }}
            >
              Discard
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              size="small"
              disabled={saving}
              sx={{ ...FONT, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', borderRadius: '8px', boxShadow: 'none' }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Role Tabs */}
      <Box sx={{ backgroundColor: '#fff', borderRadius: '14px 14px 0 0', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { ...FONT, fontSize: '0.82rem', fontWeight: 600, textTransform: 'none', minHeight: 48 },
            '& .Mui-selected': { color: '#1A8CFF' },
            '& .MuiTabs-indicator': { backgroundColor: '#1A8CFF', height: 3 },
          }}
        >
          {roles.map((role) => (
            <Tab
              key={role.roleId}
              label={role.roleName}
              sx={role.roleId === SUPER_ADMIN_ROLE_ID ? { color: '#1A8CFF !important' } : {}}
            />
          ))}
        </Tabs>
      </Box>

      {/* Permission Table */}
      <TableContainer sx={{ backgroundColor: '#fff', borderRadius: '0 0 14px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...FONT, fontSize: '0.72rem', fontWeight: 600, color: '#64748B', backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', padding: '10px 16px', minWidth: 220, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Feature
              </TableCell>
              {['view', 'edit', 'create', 'delete', 'approve'].map(action => (
                <TableCell key={action} align="center" sx={{ ...FONT, fontSize: '0.72rem', fontWeight: 600, color: ACTION_COLORS[action], backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', padding: '10px 8px', textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 80 }}>
                  {ACTION_LABELS[action]}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {FEATURE_SECTIONS.map(section => (
              <>
                <TableRow key={`section-${section.section}`}>
                  <TableCell colSpan={6} sx={{ ...FONT, fontSize: '0.75rem', fontWeight: 700, color: '#0F172A', backgroundColor: '#F1F5F9', padding: '8px 16px', borderBottom: '1px solid #E2E8F0', textTransform: 'uppercase', letterSpacing: 1 }}>
                    {section.section}
                  </TableCell>
                </TableRow>
                {section.features.map(feature => {
                  const modified = !isSuperAdmin && isModified(activeRole.roleId, feature.rightName);
                  return (
                    <TableRow key={feature.rightName} sx={{
                      backgroundColor: modified ? '#FFFBEB' : 'inherit',
                      '&:hover': { backgroundColor: modified ? '#FEF3C7' : '#FAFCFF' },
                    }}>
                      <TableCell sx={{ ...FONT, fontSize: '0.82rem', fontWeight: 500, color: '#1E293B', padding: '6px 16px 6px 28px', borderBottom: '1px solid #F1F5F9' }}>
                        {feature.label}
                        {modified && <span style={{ marginLeft: 8, fontSize: '0.65rem', color: '#D97706', fontWeight: 600 }}>modified</span>}
                      </TableCell>
                      {['view', 'edit', 'create', 'delete', 'approve'].map(action => {
                        const isApplicable = feature.actions.includes(action);
                        const checked = isApplicable && hasAction(activeRole.roleId, feature.rightName, action);
                        return (
                          <TableCell key={action} align="center" sx={{ padding: '4px 8px', borderBottom: '1px solid #F1F5F9' }}>
                            {isApplicable ? (
                              <Checkbox
                                size="small"
                                checked={checked}
                                disabled={isSuperAdmin}
                                onChange={() => handleActionToggle(activeRole.roleId, feature.rightName, action)}
                                sx={{
                                  padding: '4px',
                                  color: '#CBD5E1',
                                  '&.Mui-checked': { color: ACTION_COLORS[action] },
                                  '&.Mui-disabled.Mui-checked': { color: ACTION_COLORS[action], opacity: 0.6 },
                                }}
                              />
                            ) : (
                              <Typography sx={{ color: '#E2E8F0', fontSize: '0.75rem' }}>&mdash;</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {isSuperAdmin && (
        <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: '#EFF6FF', borderRadius: '8px', border: '1px solid #BFDBFE' }}>
          <Typography sx={{ ...FONT, fontSize: '0.78rem', color: '#1E40AF' }}>
            Super Admin has full access to all features. Permissions cannot be modified.
          </Typography>
        </Box>
      )}

      {hasChanges && (
        <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#FFFBEB', borderRadius: '8px', border: '1px solid #FDE68A', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ ...FONT, fontSize: '0.8rem', color: '#92400E', fontWeight: 500 }}>
            You have unsaved changes
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleDiscard} size="small" sx={{ ...FONT, textTransform: 'none', color: '#64748B' }}>Discard</Button>
            <Button onClick={handleSave} variant="contained" size="small" disabled={saving}
              sx={{ ...FONT, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', borderRadius: '8px', boxShadow: 'none' }}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ ...FONT, fontSize: '0.8rem' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
